import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { getSetupStatus, planOutlook } from "../../api/operatorSetup";
import { bandRange, formatInr, type TierBand } from "../../domain/tiers";

const priceOf = (b: TierBand) => (b.price === null ? "price by arrangement" : `${formatInr(b.price)} a month`);

/**
 * The operator's AnkhOS plan and what comes next (owner 2026-11-01). The band
 * follows the subscriber count, so this tells and never offers a choice.
 */
export function PlanCard() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const q = useQuery({ queryKey: ["setup", ns], enabled: Boolean(identity.token && ns), retry: false, queryFn: ({ signal }) => getSetupStatus(identity, ns, signal) });
  const count = q.data?.activesubscribers;
  if (!q.data?.tenant || count === undefined || count === null) return null;
  const o = planOutlook(count);
  return (
    <Paper sx={{ p: 2 }} data-testid="plan-card">
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
        <Typography variant="subtitle1">AnkhOS {o.band.label}: {priceOf(o.band)}</Typography>
        <Button size="small" component={RouterLink} to="/pricing">See all plans</Button>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {o.band.to === null ? `${count} active subscribers` : `${count} of ${o.band.to} active subscribers`}
      </Typography>
      {o.used !== undefined && <LinearProgress variant="determinate" value={o.used} color={o.nearLimit ? "warning" : "primary"} sx={{ my: 1 }} />}
      {o.next && (
        <Typography variant="body2" color={o.nearLimit ? "warning.main" : "text.secondary"}>
          {o.nearLimit ? "You are close to your limit. " : "Next: "}
          AnkhOS {o.next.label} covers {bandRange(o.next)} subscribers, {priceOf(o.next)}. Your plan moves by itself as you grow.
        </Typography>
      )}
    </Paper>
  );
}
